import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ImagePlus, Loader2, X } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { instructorApi, uploadInstructorFile } from "../../../services/instructorApi";
import OnboardingStepFrame from "../../../components/instructor/OnboardingStepFrame";

const GALLERY_MAX = 6;

export default function MediaStep() {
  const { instructor, refreshInstructor } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [intro, setIntro] = useState("");
  const [preview, setPreview] = useState("");
  const [fileName, setFileName] = useState("");
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryError, setGalleryError] = useState("");
  const [deletingId, setDeletingId] = useState("");

  const gallery = useMemo(
    () =>
      (instructor?.assets || [])
        .filter((a) => a.kind === "gallery" && a.public_url)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [instructor]
  );

  useEffect(() => {
    if (!instructor?.profile) return;
    setIntro(instructor.profile.intro_video_url || "");
    const url = instructor.profile.profile_image_url || "";
    if (url) {
      setPreview(url);
      setUploaded(true);
    }
  }, [instructor]);

  async function onPhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setSaving(true);
    setFileName(file.name);
    // Instant local preview while upload runs
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    try {
      const asset = await uploadInstructorFile(file, "profile_photo");
      const url = asset.public_url;
      if (!url) {
        throw new Error("Upload finished but no photo URL was returned. Please try again.");
      }
      setPreview(url);
      setUploaded(true);
      URL.revokeObjectURL(localUrl);
      await refreshInstructor();
    } catch (err) {
      setError(err.message || "Photo upload failed");
      setUploaded(Boolean(instructor?.profile?.profile_image_url));
      setPreview(instructor?.profile?.profile_image_url || "");
      URL.revokeObjectURL(localUrl);
    } finally {
      setSaving(false);
      // Reset native input so the same file can be re-selected later.
      // Do not treat this as "upload failed" — the preview/success state is the source of truth.
      e.target.value = "";
    }
  }

  async function onGalleryChange(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setGalleryError("");

    const remaining = GALLERY_MAX - gallery.length;
    if (remaining <= 0) {
      setGalleryError(`You can upload up to ${GALLERY_MAX} photos.`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      setGalleryError(`Only ${remaining} more photo${remaining === 1 ? "" : "s"} allowed — extras were skipped.`);
    }

    setGalleryUploading(true);
    try {
      for (const file of toUpload) {
        try {
          await uploadInstructorFile(file, "gallery");
        } catch (err) {
          setGalleryError(err.message || "One of the photos failed to upload.");
        }
      }
      await refreshInstructor();
    } finally {
      setGalleryUploading(false);
    }
  }

  async function onGalleryDelete(id) {
    setGalleryError("");
    setDeletingId(id);
    try {
      await instructorApi.deleteAsset(id);
      await refreshInstructor();
    } catch (err) {
      setGalleryError(err.message || "Could not remove that photo.");
    } finally {
      setDeletingId("");
    }
  }

  async function handleContinue(e) {
    e.preventDefault();
    setError("");
    if (!preview && !instructor?.profile?.profile_image_url) {
      setError("Please upload a profile photo before continuing.");
      return;
    }
    setSaving(true);
    try {
      // Only save intro video here — photo is already persisted by the upload endpoint.
      await instructorApi.saveStep("media", { intro_video_url: intro || null });
      await refreshInstructor();
      navigate("/instructor/onboarding/offerings");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <OnboardingStepFrame
      title="Photo that feels like you"
      subtitle="Use a clear, face-forward headshot. Most image formats work (JPG, PNG, WebP, GIF, BMP, AVIF, HEIC)."
    >
      {error && <div className="mb-4 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
      <form onSubmit={handleContinue} className="bg-white border border-emerald-100 rounded-2xl p-6 space-y-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-28 h-28 rounded-full bg-gray-100 overflow-hidden border border-gray-200 shrink-0">
            {preview ? (
              <img src={preview} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">No photo</div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 mb-1">Profile photo</p>
            {uploaded && preview ? (
              <p className="text-sm text-emerald-700 inline-flex items-center gap-1.5 mb-3">
                <CheckCircle2 size={15} />
                Photo uploaded{fileName ? ` · ${fileName}` : ""}
              </p>
            ) : (
              <p className="text-sm text-gray-500 mb-3">
                JPG, PNG, WebP, GIF, HEIC and similar · face clearly visible
              </p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.jpg,.jpeg,.png,.webp,.gif,.bmp,.avif,.heic,.heif,.tif,.tiff"
              disabled={saving}
              onChange={onPhotoChange}
              className="sr-only"
              aria-label="Upload profile photo"
            />
            <button
              type="button"
              disabled={saving}
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-2 text-sm font-medium hover:bg-emerald-100 disabled:opacity-60"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <ImagePlus size={16} /> {uploaded ? "Replace photo" : "Choose photo"}
                </>
              )}
            </button>
            {/* Intentionally hide the browser "No file chosen" label — it resets after every upload and looks like a failure. */}
          </div>
        </div>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Intro video URL (optional)</span>
          <input
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            placeholder="https://youtube.com/..."
          />
        </label>

        <div className="border-t border-gray-100 pt-6">
          <div className="flex items-center justify-between gap-3 mb-1">
            <p className="font-medium text-gray-900">More photos (optional)</p>
            <span className="text-xs text-gray-500">
              {gallery.length}/{GALLERY_MAX}
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            Show your studio, classes, or retreats. These appear as a gallery on your public profile.
          </p>

          {galleryError && (
            <div className="mb-3 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{galleryError}</div>
          )}

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {gallery.map((img) => (
              <div
                key={img.id}
                className="relative group aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-100"
              >
                <img src={img.public_url} alt="Gallery" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => onGalleryDelete(img.id)}
                  disabled={deletingId === img.id}
                  aria-label="Remove photo"
                  className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition hover:bg-black/75 disabled:opacity-100"
                >
                  {deletingId === img.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <X size={14} />
                  )}
                </button>
              </div>
            ))}

            {gallery.length < GALLERY_MAX && (
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                disabled={galleryUploading}
                className="aspect-square rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/50 text-emerald-700 flex flex-col items-center justify-center gap-1.5 text-xs font-medium hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-60"
              >
                {galleryUploading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <ImagePlus size={20} />
                )}
                <span>{galleryUploading ? "Uploading…" : "Add photos"}</span>
              </button>
            )}
          </div>

          <input
            ref={galleryInputRef}
            type="file"
            multiple
            accept="image/*,.jpg,.jpeg,.png,.webp,.gif,.bmp,.avif,.heic,.heif,.tif,.tiff"
            disabled={galleryUploading}
            onChange={onGalleryChange}
            className="sr-only"
            aria-label="Upload gallery photos"
          />
        </div>

        <div className="flex justify-between pt-2">
          <Link to="/instructor/onboarding/credentials" className="text-sm text-gray-600 hover:underline">
            Back
          </Link>
          <button type="submit" disabled={saving} className="rounded-lg bg-emerald-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">
            {saving ? "Saving…" : "Save & continue"}
          </button>
        </div>
      </form>
    </OnboardingStepFrame>
  );
}
